import {
  buildScopedUpdateProjection,
  buildScopeProjection,
  type CatalogEntry,
  describeJevPicks,
  pickCatalogProduct,
  SCOPE_PROJECTION_NONE_KEY,
  type ScopeProjection,
} from './scopeProjectionLib';
import { firstSpokenMoney, firstSpokenNumber, spokenAmountForValueType } from './spokenNumberLib';
import { buildProposalSummary } from './voiceScopeSummaryLib';
import {
  DEFAULT_VOICE_SCOPE_ORCHESTRATOR_CONFIG,
  type JevAnswer,
  type JevEvaluateResponse,
  type VoiceScopeAddOptionCommand,
  type VoiceScopeCommand,
  type VoiceScopeOrchestratorConfig,
  type VoiceScopeOrchestratorOutcome,
  type VoiceScopeProposeOutcome,
  type VoiceScopeSetTaskValueCommand,
  type VoiceScopeSnapshot,
  type VoiceScopeSnapshotTask,
} from './voiceScopeTypes';

export type VoiceScopeSessionState = {
  snapshot: VoiceScopeSnapshot;
  transcript: string[];
  recentEdits: string[];
};

export type EvaluateJevFn = (
  state: string,
  questions: ScopeProjection['questions'],
) => Promise<JevEvaluateResponse>;

const percent = (probability: number) => `${Math.round(probability * 100)}%`;

export const jevProbabilityOf = (answer: JevAnswer | undefined, category: string): number =>
  answer?.probabilities?.[category] ?? (answer?.choice === category ? answer.confidence ?? 0 : 0);

const confidentPick = (answer: JevAnswer | undefined, threshold: number): string | undefined => {
  if (!answer?.choice || answer.choice === SCOPE_PROJECTION_NONE_KEY) return undefined;
  return jevProbabilityOf(answer, answer.choice) >= threshold ? answer.choice : undefined;
};

const impliedUpdateMinimumShare = 0.2;
const dominantMinimumMass = 0.5;
const dominanceOverNone = 1.5;
const recencyTieBreakMinimumShare = 0.2;

const impliedUpdate = (operation: JevAnswer, answers: Record<string, JevAnswer>, utterance: string, threshold: number): string | undefined => {
  const spokeMoney = firstSpokenMoney(utterance) != null;
  if (
    operation.choice !== SCOPE_PROJECTION_NONE_KEY
    && operation.choice !== 'update'
  ) return undefined;
  if (
    !spokeMoney
    && jevProbabilityOf(operation, 'update') < impliedUpdateMinimumShare
  ) return undefined;
  const target = answers.targetValue;
  if (!target?.choice || target.choice === SCOPE_PROJECTION_NONE_KEY) return undefined;
  if (jevProbabilityOf(target, target.choice) < threshold) return undefined;
  if (!spokeMoney && firstSpokenNumber(utterance) == null) return undefined;
  return 'update';
};

const clearlyBeatsNone = (mass: number, noneMass: number) => mass >= dominantMinimumMass && mass >= dominanceOverNone * noneMass;

const mintCommandId = () => crypto.randomUUID();

const buildAddCommand = (
  entry: CatalogEntry,
  groupId: string,
  utterance: string,
  taskId: string,
  lineItem: ReturnType<typeof pickCatalogProduct>,
  quantity?: number,
  spokenPrice?: number,
): VoiceScopeAddOptionCommand => ({
  type: 'addOption',
  commandId: mintCommandId(),
  groupId,
  taskId,
  taskTemplateId: entry.task.templateId,
  taskName: entry.task.name,
  optionTemplateId: entry.option.templateId,
  optionName: entry.option.name,
  lineItemTemplateId: lineItem?.templateId,
  lineItemName: lineItem?.name,
  quantity,
  spokenPrice,
  utterance,
});

const buildSetValueCommand = (
  target: { task: VoiceScopeSnapshotTask; value: VoiceScopeSnapshotTask['values'][number] },
  groupId: string,
  utterance: string,
  value: number,
): VoiceScopeSetTaskValueCommand => ({
  type: 'setTaskValue',
  commandId: mintCommandId(),
  groupId,
  taskId: target.task.id,
  taskName: target.task.name,
  utterance,
  valueName: target.value.name,
  valueLabel: target.value.description ?? target.value.name,
  valueType: target.value.type,
  value,
  optionId: target.value.optionId,
  lineItemId: target.value.lineItemId,
});

type CatalogPickResult = { picks: CatalogEntry[]; closest: string; bestGuess?: CatalogEntry };

const rankCatalogOptions = (
  answer: JevAnswer | undefined,
  projection: ScopeProjection,
) => {
  const probabilities = answer?.probabilities;
  if (!probabilities) return { options: [], closest: '', bestGuess: undefined as CatalogEntry | undefined };

  const linesByOption = new Map<string, { entry: CatalogEntry; probability: number }[]>();
  for (const [key, probability] of Object.entries(probabilities)) {
    const entry = projection.catalogByKey[key];
    if (!entry) continue;
    const existing = linesByOption.get(entry.optionKey) ?? [];
    existing.push({ entry, probability });
    linesByOption.set(entry.optionKey, existing);
  }

  const options = [...linesByOption.values()]
    .map(lines => ({
      lines: lines.sort((left, right) => right.probability - left.probability),
      mass: lines.reduce((total, line) => total + line.probability, 0),
    }))
    .sort((left, right) => right.mass - left.mass);

  const closest = options.slice(0, 2)
    .map(option => `"${option.lines[0].entry.optionLabel}" ${percent(option.mass)}`)
    .join(', ');

  return { options, closest, bestGuess: options[0]?.lines[0]?.entry };
};

const catalogPicks = (
  answer: JevAnswer | undefined,
  projection: ScopeProjection,
  compoundPickThreshold: number,
  confidenceThreshold: number,
): CatalogPickResult => {
  const { options, closest, bestGuess } = rankCatalogOptions(answer, projection);
  const probabilities = answer?.probabilities;
  if (!probabilities) return { picks: [], closest: '', bestGuess };

  const confident = options.filter(option => option.mass >= compoundPickThreshold);
  const confidentMass = confident.reduce((total, option) => total + option.mass, 0);
  const noneMass = probabilities[SCOPE_PROJECTION_NONE_KEY] ?? 0;
  if (confidentMass < confidenceThreshold && !clearlyBeatsNone(confidentMass, noneMass)) {
    return { picks: [], closest, bestGuess };
  }

  const distinctLines = (lines: { entry: CatalogEntry; probability: number }[]): CatalogEntry[] => {
    const seenLabels = new Set<string>();
    const labor = lines.filter(line => line.entry.lineItem?.isProduct !== true);
    const products = lines.filter(line => line.entry.lineItem?.isProduct === true);
    const strongLabor = labor
      .filter(line => line.probability >= compoundPickThreshold && !seenLabels.has(line.entry.label) && seenLabels.add(line.entry.label))
      .map(line => line.entry);
    const productMass = products.reduce((total, line) => total + line.probability, 0);
    const bestProduct = productMass >= compoundPickThreshold && products[0] ? [products[0].entry] : [];
    const picks = [...strongLabor, ...bestProduct];
    return picks.length > 0 ? picks : [lines[0].entry];
  };

  return { picks: confident.flatMap(option => distinctLines(option.lines)), closest, bestGuess };
};

type ValueTarget = { task: VoiceScopeSnapshotTask; value: VoiceScopeSnapshotTask['values'][number] };

const valueTargetBestEffort = (
  answer: JevAnswer | undefined,
  projection: ScopeProjection,
  recentTaskId?: string,
): ValueTarget | undefined => {
  const probabilities = answer?.probabilities;
  if (!probabilities) return undefined;

  if (recentTaskId) {
    const recentTarget = Object.entries(probabilities)
      .filter(([category, probability]) => category !== SCOPE_PROJECTION_NONE_KEY && probability >= recencyTieBreakMinimumShare)
      .sort((left, right) => right[1] - left[1])
      .map(([category]) => projection.valueByKey[category])
      .find(target => target?.task.id === recentTaskId);
    if (recentTarget) return recentTarget;
  }

  const [bestKey] = Object.entries(probabilities)
    .filter(([category]) => category !== SCOPE_PROJECTION_NONE_KEY)
    .sort((left, right) => right[1] - left[1]);
  return bestKey ? projection.valueByKey[bestKey[0]] : undefined;
};

const confidentValueTarget = (
  answer: JevAnswer | undefined,
  projection: ScopeProjection,
  threshold: number,
  recentTaskId?: string,
): ValueTarget | undefined => {
  const key = confidentPick(answer, threshold);
  if (key && projection.valueByKey[key]) return projection.valueByKey[key];

  const probabilities = answer?.probabilities;
  if (!probabilities || !recentTaskId) return undefined;

  return Object.entries(probabilities)
    .filter(([category, probability]) => category !== SCOPE_PROJECTION_NONE_KEY && probability >= recencyTieBreakMinimumShare)
    .sort((left, right) => right[1] - left[1])
    .map(([category]) => projection.valueByKey[category])
    .find(target => target?.task.id === recentTaskId);
};

const utteranceSoundsLikeAdd = (utterance: string) => /\b(replace|install|add|new|demo|remove|put in|swap)\b/i.test(utterance);

const buildProposeOutcome = (
  utterance: string,
  commands: VoiceScopeCommand[],
  summary: string,
  scanPartId: string,
  operationConfidence: number,
  operationGatePassed: boolean,
  usedBestGuess: boolean,
): VoiceScopeOrchestratorOutcome => ({
  kind: 'propose',
  utterance,
  commands,
  summary,
  scanPartId,
  confidence: operationConfidence,
  operationGatePassed,
  usedBestGuess,
  jevDetail: '',
});

const buildAddProposal = (
  entries: CatalogEntry[],
  utterance: string,
  groupId: string,
  operationConfidence: number,
  operationGatePassed: boolean,
  usedBestGuess: boolean,
): VoiceScopeOrchestratorOutcome => {
  // One spoken quantity/price can't be split across several lines, so only a single add takes them.
  const isSingleAdd = entries.length === 1;
  const spokenPrice = isSingleAdd ? firstSpokenMoney(utterance) : undefined;
  // Picks for a task not yet in the room share one new task id, so the task is created once.
  const newTaskIdByTemplateId = new Map<string, string>();
  const taskIdFor = ({ task }: CatalogEntry) => {
    if (task.existingTaskId) return task.existingTaskId;
    const taskId = newTaskIdByTemplateId.get(task.templateId) ?? crypto.randomUUID();
    newTaskIdByTemplateId.set(task.templateId, taskId);
    return taskId;
  };
  const commands = entries.map(entry => {
    const lineItem = pickCatalogProduct(entry, utterance);
    const quantity = isSingleAdd ? firstSpokenNumber(utterance, lineItem?.name ?? entry.label) : undefined;
    return buildAddCommand(entry, groupId, utterance, taskIdFor(entry), lineItem, quantity, spokenPrice);
  });
  return buildProposeOutcome(
    utterance,
    commands,
    buildProposalSummary(commands),
    commands[0].taskId,
    operationConfidence,
    operationGatePassed,
    usedBestGuess,
  );
};

const tryAddFallback = (
  answers: Record<string, JevAnswer>,
  projection: ScopeProjection,
  utterance: string,
  groupId: string,
  config: VoiceScopeOrchestratorConfig,
  operationConfidence: number,
): VoiceScopeOrchestratorOutcome | undefined => {
  if (!utteranceSoundsLikeAdd(utterance)) return undefined;

  const { picks, bestGuess } = catalogPicks(
    answers.catalogOption,
    projection,
    config.compoundPickThreshold,
    config.confidenceThreshold,
  );
  const operationGatePassed = operationConfidence >= config.confidenceThreshold;
  if (picks.length > 0) {
    return buildAddProposal(picks, utterance, groupId, operationConfidence, operationGatePassed, false);
  }
  if (!bestGuess) return undefined;
  return buildAddProposal([bestGuess], utterance, groupId, operationConfidence, operationGatePassed, true);
};

const decideAdd = (
  answers: Record<string, JevAnswer>,
  projection: ScopeProjection,
  utterance: string,
  groupId: string,
  config: VoiceScopeOrchestratorConfig,
  operationConfidence: number,
): VoiceScopeOrchestratorOutcome => {
  const { picks, closest, bestGuess } = catalogPicks(
    answers.catalogOption,
    projection,
    config.compoundPickThreshold,
    config.confidenceThreshold,
  );
  const operationGatePassed = operationConfidence >= config.confidenceThreshold;
  if (picks.length === 0) {
    if (bestGuess && operationGatePassed) {
      return buildAddProposal([bestGuess], utterance, groupId, operationConfidence, operationGatePassed, true);
    }
    const operationNote = operationConfidence >= config.confidenceThreshold
      ? `Operation add was ${percent(operationConfidence)} confident, but`
      : `Operation add was only ${percent(operationConfidence)} confident and`;
    return {
      kind: 'reject',
      utterance,
      reason: `${operationNote} no catalog match cleared the confidence bar${closest ? ` (closest: ${closest})` : ''}`,
      ...(operationConfidence < config.confidenceThreshold ? { confidence: operationConfidence } : {}),
    };
  }

  return buildAddProposal(
    picks,
    utterance,
    groupId,
    operationConfidence,
    operationGatePassed,
    false,
  );
};

const decideUpdate = (
  answers: Record<string, JevAnswer>,
  projection: ScopeProjection,
  utterance: string,
  groupId: string,
  config: VoiceScopeOrchestratorConfig,
  operationConfidence: number,
  recentTaskId?: string,
  allowAddFallback = true,
): VoiceScopeOrchestratorOutcome => {
  const confidentTarget = confidentValueTarget(answers.targetValue, projection, config.confidenceThreshold, recentTaskId);

  if (allowAddFallback && !confidentTarget && utteranceSoundsLikeAdd(utterance)) {
    const addFallback = tryAddFallback(answers, projection, utterance, groupId, config, operationConfidence);
    if (addFallback) return addFallback;
  }

  const bestTarget = confidentTarget ?? valueTargetBestEffort(answers.targetValue, projection, recentTaskId);

  if (!bestTarget) {
    return {
      kind: 'reject',
      utterance,
      reason: `"${utterance}": couldn't tell which measurement to change — try naming the task or saying "add" with what to do`,
    };
  }

  const number = spokenAmountForValueType(utterance, bestTarget.value.type);
  if (number == null) {
    if (allowAddFallback) {
      const addFallback = tryAddFallback(answers, projection, utterance, groupId, config, operationConfidence);
      if (addFallback) return addFallback;
    }
    return {
      kind: 'reject',
      utterance,
      reason: `"${utterance}": no number heard for ${bestTarget.value.description ?? bestTarget.value.name}`,
    };
  }

  const command = buildSetValueCommand(bestTarget, groupId, utterance, number);
  const summary = buildProposalSummary([command]);
  const operationGatePassed = operationConfidence >= config.confidenceThreshold;
  const usedBestGuess = !confidentTarget;
  return buildProposeOutcome(
    utterance,
    [command],
    summary,
    bestTarget.task.id,
    operationConfidence,
    operationGatePassed,
    usedBestGuess,
  );
};

const decide = (
  answers: Record<string, JevAnswer>,
  projection: ScopeProjection,
  utterance: string,
  groupId: string,
  config: VoiceScopeOrchestratorConfig,
  recentTaskId?: string,
): VoiceScopeOrchestratorOutcome => {
  const operation = answers.operation;
  if (!operation?.choice) return { kind: 'reject', utterance, reason: 'KaiAgent gave no operation answer' };

  const choice = impliedUpdate(operation, answers, utterance, config.confidenceThreshold) ?? operation.choice;
  const operationConfidence = jevProbabilityOf(operation, choice);

  if (choice === SCOPE_PROJECTION_NONE_KEY) {
    return { kind: 'ignore', utterance };
  }

  if (choice !== operation.choice && operationConfidence < config.confidenceThreshold) {
    return {
      kind: 'reject',
      utterance,
      reason: `unsure what change was asked for (${choice} ${percent(operationConfidence)})`,
      confidence: operationConfidence,
    };
  }

  if (choice === 'add') return decideAdd(answers, projection, utterance, groupId, config, operationConfidence);
  if (choice === 'update') {
    return decideUpdate(answers, projection, utterance, groupId, config, operationConfidence, recentTaskId);
  }
  return { kind: 'reject', utterance, reason: `unsupported operation ${choice}` };
};

const decideScopedUpdate = (
  answers: Record<string, JevAnswer>,
  projection: ScopeProjection,
  utterance: string,
  groupId: string,
  config: VoiceScopeOrchestratorConfig,
  recentTaskId?: string,
): VoiceScopeProposeOutcome | undefined => {
  const operation = answers.operation;
  if (!operation?.choice || operation.choice === SCOPE_PROJECTION_NONE_KEY) return undefined;

  const choice = impliedUpdate(operation, answers, utterance, config.confidenceThreshold) ?? operation.choice;
  if (choice !== 'update') return undefined;

  const operationConfidence = jevProbabilityOf(operation, choice);
  const outcome = decideUpdate(
    answers,
    projection,
    utterance,
    groupId,
    config,
    operationConfidence,
    recentTaskId,
    false,
  );
  return outcome.kind === 'propose' ? outcome : undefined;
};

const voiceScopeCommandKey = (command: VoiceScopeCommand): string =>
  command.type === 'setTaskValue'
    ? `set:${command.taskId}:${command.valueName}:${command.optionId ?? ''}:${command.lineItemId ?? ''}`
    : `add:${command.taskTemplateId}:${command.optionTemplateId}:${command.lineItemTemplateId ?? ''}`;

const isSetTaskValue = (command: VoiceScopeCommand) => command.type === 'setTaskValue';

/**
 * Merges the full-channel proposal with the scoped-update one. One spoken number is one edit, so
 * when the scoped channel picked a value to set, the full channel's own value pick is dropped
 * instead of writing the same number to a second field.
 */
const mergeProposals = (
  full: VoiceScopeProposeOutcome,
  scoped: VoiceScopeProposeOutcome,
  utterance: string,
): VoiceScopeProposeOutcome => {
  const fullCommands = scoped.commands.some(isSetTaskValue) ? full.commands.filter(command => !isSetTaskValue(command)) : full.commands;
  if (fullCommands.length === 0) return scoped;

  const commandsByKey = new Map<string, VoiceScopeCommand>();
  for (const command of [...fullCommands, ...scoped.commands]) {
    commandsByKey.set(voiceScopeCommandKey(command), command);
  }
  const commands = [...commandsByKey.values()];

  return {
    kind: 'propose',
    utterance,
    commands,
    summary: buildProposalSummary(commands),
    confidence: Math.min(full.confidence, scoped.confidence),
    operationGatePassed: full.operationGatePassed && scoped.operationGatePassed,
    usedBestGuess: full.usedBestGuess || scoped.usedBestGuess,
    jevDetail: `${full.jevDetail} | ${scoped.jevDetail}`,
    scanPartId: commands[0]?.taskId ?? full.scanPartId,
  };
};

const mergeOrchestratorOutcomes = (
  primary: VoiceScopeOrchestratorOutcome,
  scopedUpdate: VoiceScopeProposeOutcome | undefined,
  utterance: string,
): VoiceScopeOrchestratorOutcome => {
  if (!scopedUpdate) return primary;
  if (primary.kind === 'propose') return mergeProposals(primary, scopedUpdate, utterance);
  return scopedUpdate;
};

const enrichProposeOutcome = (
  rawOutcome: VoiceScopeProposeOutcome,
  response: JevEvaluateResponse,
  projection: ScopeProjection,
  utterance: string,
  config: VoiceScopeOrchestratorConfig,
  channel: 'full' | 'scoped update',
): VoiceScopeProposeOutcome => {
  const operation = response.answers.operation;
  const resolvedChoice = operation?.choice
    ? impliedUpdate(operation, response.answers, utterance, config.confidenceThreshold) ?? operation.choice
    : undefined;
  const operationConfidence = resolvedChoice ? jevProbabilityOf(operation, resolvedChoice) : rawOutcome.confidence;
  const operationGatePassed = operationConfidence >= config.confidenceThreshold;

  return {
    ...rawOutcome,
    confidence: operationConfidence,
    operationGatePassed,
    jevDetail: [
      `${channel}:`,
      resolvedChoice ? `operation ${resolvedChoice} ${percent(operationConfidence)}` : undefined,
      operationGatePassed ? 'operation gate: pass (≥60%)' : 'operation gate: needs review (<60%)',
      rawOutcome.usedBestGuess ? 'target: best guess' : undefined,
      'result: waiting for your review',
      describeJevPicks(response.answers, projection),
    ].filter(Boolean).join(' · '),
  };
};

const enrichOutcome = (
  rawOutcome: VoiceScopeOrchestratorOutcome,
  response: JevEvaluateResponse,
  projection: ScopeProjection,
  utterance: string,
  config: VoiceScopeOrchestratorConfig,
  channel: 'full' | 'scoped update',
): VoiceScopeOrchestratorOutcome =>
  rawOutcome.kind === 'propose'
    ? enrichProposeOutcome(rawOutcome, response, projection, utterance, config, channel)
    : rawOutcome;

/** Processes one finalized utterance against the current voice-scope session. */
export const processVoiceScopeUtterance = async (
  utterance: string,
  session: VoiceScopeSessionState,
  evaluateJev: EvaluateJevFn,
  config: VoiceScopeOrchestratorConfig = DEFAULT_VOICE_SCOPE_ORCHESTRATOR_CONFIG,
  recentTaskId?: string,
): Promise<{ outcome: VoiceScopeOrchestratorOutcome; transcript: string[] }> => {
  const trimmed = utterance.trim();
  if (!/[a-z0-9]/i.test(trimmed)) {
    return { outcome: { kind: 'ignore', utterance: trimmed }, transcript: session.transcript };
  }

  const projectionInput = {
    groupName: session.snapshot.group.name,
    tasks: session.snapshot.group.tasks,
    transcript: session.transcript,
    recentEdits: session.recentEdits.slice(-config.recentEditsWindow),
    utterance: trimmed,
  };

  const fullProjection = buildScopeProjection({
    ...projectionInput,
    catalog: session.snapshot.catalog,
  });
  const scopedUpdateProjection = buildScopedUpdateProjection(projectionInput);

  const [fullResponse, scopedUpdateResponse] = await Promise.all([
    evaluateJev(fullProjection.state, fullProjection.questions),
    scopedUpdateProjection
      ? evaluateJev(scopedUpdateProjection.state, scopedUpdateProjection.questions)
      : Promise.resolve(undefined),
  ]);

  const primaryRaw = decide(
    fullResponse.answers,
    fullProjection,
    trimmed,
    session.snapshot.group.id,
    config,
    recentTaskId,
  );
  const primaryOutcome = enrichOutcome(primaryRaw, fullResponse, fullProjection, trimmed, config, 'full');

  const scopedUpdateRaw = scopedUpdateProjection && scopedUpdateResponse
    ? decideScopedUpdate(
      scopedUpdateResponse.answers,
      scopedUpdateProjection,
      trimmed,
      session.snapshot.group.id,
      config,
      recentTaskId,
    )
    : undefined;
  const scopedUpdateOutcome = scopedUpdateRaw && scopedUpdateResponse && scopedUpdateProjection
    ? enrichProposeOutcome(scopedUpdateRaw, scopedUpdateResponse, scopedUpdateProjection, trimmed, config, 'scoped update')
    : undefined;

  const outcome = mergeOrchestratorOutcomes(primaryOutcome, scopedUpdateOutcome, trimmed);
  const transcript = [...session.transcript, trimmed].slice(-config.transcriptWindow);
  return { outcome, transcript };
};
